import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  enregistrerFuseau,
  enregistrerFuseauAthlete,
  estCoach,
  nomAthlete,
  useMoi,
  type AthleteRattache,
} from '../Auth/moi.ts';
import { aujourdhuiDans, fuseauNavigateur, fuseauxDisponibles } from '../Planning/dates.ts';

function libelleJour(jour: string): string {
  return new Date(jour + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}

// Fuseau horaire de l'utilisateur (page Mes preferences) : le jour courant
// de son calendrier y est calcule (les jours passes ne se modifient plus),
// y compris quand son coach l'etablit depuis un autre fuseau. Renseigne a la
// connexion avec le fuseau du navigateur. Le coach peut aussi changer celui
// d'un de ses athletes (page Athletes, parametre athlete).
export default function FuseauHoraireEditeur({ athlete = null }: { athlete?: AthleteRattache | null }) {
  const moi = useMoi();
  const navigateur = fuseauNavigateur();
  const fuseaux = React.useMemo(() => fuseauxDisponibles(), []);
  const memorise: string | null = athlete ? athlete.fuseau ?? null : moi?.fuseau ?? null;
  const [valeur, setValeur] = React.useState<string | null>(memorise);
  const [etat, setEtat] = React.useState<'' | 'enregistrement' | 'enregistre' | 'erreur'>('');
  const [erreur, setErreur] = React.useState<string>('');

  React.useEffect(() => setValeur(memorise), [memorise]);

  const enregistrer = (f: string | null) => {
    setValeur(f);
    if (!f) return;
    setEtat('enregistrement');
    (athlete?.ssoUserId ? enregistrerFuseauAthlete(athlete.ssoUserId, f) : enregistrerFuseau(f))
      .then(() => setEtat('enregistre'))
      .catch((e: Error) => {
        setErreur(e.message);
        setEtat('erreur');
      });
  };

  return (
    <Box id="preference-fuseau" sx={{ mt: 4 }}>
      <h2>Fuseau horaire</h2>
      <Typography sx={{ mb: 2 }} color="text.secondary">
        {athlete ? (
          <>
            Le calendrier de {nomAthlete(athlete)} suit ce fuseau horaire, même si tu es ailleurs : une séance d'un
            jour passé (y compris la veille) ne peut plus être modifiée, déplacée ni retirée.
            {!athlete.fuseauRenseigne && " Il n'est pas encore renseigné : c'est le fuseau par défaut."}
          </>
        ) : (
          <>
            Ton calendrier suit ce fuseau horaire : une séance d'un jour passé (y compris la veille) ne peut plus être
            modifiée, déplacée ni retirée.{' '}
            {estCoach(moi)
              ? 'Le calendrier de chacun de tes athlètes suit son propre fuseau horaire (page Athlètes), même si tu es ailleurs.'
              : 'Ton coach manipule ton calendrier selon ce fuseau, même s’il est ailleurs.'}
          </>
        )}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', maxWidth: 640 }}>
        <Autocomplete
          options={fuseaux}
          value={valeur}
          onChange={(_e, f) => enregistrer(f)}
          sx={{ minWidth: 300, flex: 1 }}
          renderInput={(params) => <TextField {...params} label="Fuseau horaire" />}
        />
        {valeur !== navigateur && (
          <Button variant="outlined" onClick={() => enregistrer(navigateur)}>
            Utiliser celui de ce navigateur ({navigateur})
          </Button>
        )}
      </Box>

      {valeur && (
        <Typography sx={{ mt: 2 }}>
          Aujourd'hui dans ce fuseau : <b>{libelleJour(aujourdhuiDans(valeur))}</b>
        </Typography>
      )}
      <Typography variant="caption" color={etat === 'erreur' ? 'error' : 'text.secondary'}>
        {etat === 'enregistrement' && 'Enregistrement…'}
        {etat === 'enregistre' && 'Enregistré'}
        {etat === 'erreur' && `Enregistrement impossible : ${erreur}`}
      </Typography>
    </Box>
  );
}
